import { useState } from "react";
import { FileDown, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { exportPDF, exportCSV, exportXLSX } from "@/lib/exportUtils";

// A compact bar of Export PDF / CSV / XLSX buttons.
// `buildReport()` should return a fresh report definition each time so the
// export always uses the latest filtered data.
const ExportBar = ({ buildReport, pdfOverride, availablePDF = true, availableCSV = true, availableXLSX = true, testIdPrefix = "export" }) => {
  const [busy, setBusy] = useState(null);

  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "pdf" && typeof pdfOverride === "function") {
        pdfOverride();
      } else {
        const rep = buildReport(kind);
        if (!rep) { toast.error("Nothing to export"); return; }
        if (kind === "pdf")  exportPDF(rep);
        if (kind === "csv")  exportCSV(rep);
        if (kind === "xlsx") await exportXLSX(rep);
      }
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      console.error(e);
      toast.error(`Could not generate ${kind.toUpperCase()}`);
    } finally { setBusy(null); }
  };

  const btnClass = "flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-slate-50 text-xs font-semibold text-slate-600 hover:text-[#193f7d] transition-colors disabled:opacity-60";

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testIdPrefix}-bar`}>
      {availablePDF && (
        <button onClick={() => run("pdf")} disabled={busy !== null} className={btnClass} data-testid={`${testIdPrefix}-pdf-btn`}>
          <FileDown className="w-3.5 h-3.5" />
          {busy === "pdf" ? "Generating…" : "PDF"}
        </button>
      )}
      {availableCSV && (
        <button onClick={() => run("csv")} disabled={busy !== null} className={btnClass} data-testid={`${testIdPrefix}-csv-btn`}>
          <FileText className="w-3.5 h-3.5" />
          {busy === "csv" ? "Generating…" : "CSV"}
        </button>
      )}
      {availableXLSX && (
        <button onClick={() => run("xlsx")} disabled={busy !== null} className={btnClass} data-testid={`${testIdPrefix}-xlsx-btn`}>
          <FileSpreadsheet className="w-3.5 h-3.5" />
          {busy === "xlsx" ? "Generating…" : "XLSX"}
        </button>
      )}
    </div>
  );
};

export default ExportBar;
